import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { SearchField } from "@/components/SearchField/SearchField";
import { TablePagination } from "@/components/TablePagination/TablePagination";

import { useGetScoringHistoryQuery } from "@/pages/Private/redux/score/score.api";

import { initialPageSize } from "@/utils/preferredPageSize";

import { ScoreShell } from "../../shell/ScoreShell";
import { useDebouncedValue } from "../../utils/debounceValue";

import { ChangeLogRow } from "./ChangeLogRow";
import { groupHistory } from "./groupHistory";

const COLUMNS = ["lead", "date", "previous", "updated", "userNote", "updatedBy"] as const;

const HEADER = "px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-text-meta";

const dateBox = (value: string, onChange: (value: string) => void, name: string) => (
	<div className="flex items-center gap-2 rounded-lg border border-border-light bg-white px-3 py-2">
		<Icon className="h-3.5 w-3.5 shrink-0 text-text-disabled" icon={faCalendar} />
		<input
			className="text-sm text-text outline-none"
			data-testid={name}
			type="date"
			value={value}
			onChange={(event) => onChange(event.target.value)}
		/>
	</div>
);

/**
 * Change log (PREC-4055): every manual override of a lead's classification,
 * searchable by lead and bounded by date, in the settings frame.
 */
export const ScoreChangeLog: FunctionComponent = () => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [page, setPage] = useState(1);
	// Its selector remembers a pick for every table, so it starts from that pick too (PREC-4403).
	const [limit, setLimit] = useState(() => initialPageSize({ fallback: 15 }));
	const debouncedSearch = useDebouncedValue(search, 500);

	const { data: history } = useGetScoringHistoryQuery(
		{
			searchValue: debouncedSearch || undefined,
			dateFrom: dateFrom || undefined,
			dateTo: dateTo || undefined,
			page,
			limit,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const groups = useMemo(() => groupHistory(history?.data ?? []), [history]);

	useEffect(() => {
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setPage(1);
	}, [debouncedSearch, dateFrom, dateTo]);

	return (
		<ScoreShell
			description={t("leadScoring.changeLogDescription")}
			intro={t("leadScoring.changeLogTitle")}
			step="changelog"
		>
			<div className="mb-4 flex flex-wrap items-center gap-3">
				<div className="w-64">
					<SearchField
						handleChange={(event) => setSearch(event.target.value)}
						handleReset={() => setSearch("")}
						name="changelog-search"
						placeholder={t("leadScoring.changeLog.searchPlaceholder")}
						testId="changelog-search"
						value={search}
					/>
				</div>
				{dateBox(dateFrom, setDateFrom, "changelog-date-from")}
				<span className="text-text-disabled">→</span>
				{dateBox(dateTo, setDateTo, "changelog-date-to")}
			</div>

			<div className="w-full overflow-hidden rounded-xl border border-gray-200">
				<table className="w-full border-collapse" data-testid="changelog-table">
					<thead>
						<tr className="border-b border-border-light bg-background-light">
							{COLUMNS.map((column) => (
								<th key={column} className={HEADER}>
									{t(`leadScoring.changeLog.${column}`)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{groups.length === 0 ? (
							<tr>
								<td
									className="px-4 py-8 text-center text-sm text-text-disabled"
									colSpan={COLUMNS.length}
								>
									{t("leadScoring.changeLog.empty")}
								</td>
							</tr>
						) : (
							groups.map((group) => <ChangeLogRow key={group.key} group={group} />)
						)}
					</tbody>
				</table>
			</div>

			<TablePagination
				className="mt-4 flex justify-between"
				currentPage={page}
				handleChangeRowsPerPage={(rowsPerPage) => {
					setLimit(rowsPerPage);
					setPage(1);
				}}
				handleNavigateToPage={setPage}
				limit={limit}
				maxPage={history?.meta?.pageCount ?? 1}
			/>
		</ScoreShell>
	);
};
